import api from './api'

// ── Per-server billing (billing skeleton) ──
// Mounted at /api/servers/:serverId/billing — server owner or global admin.
// Money is integer cents everywhere (server converts BigInt → Number at the
// JSON boundary). Server error codes arrive in err.response.data.error.

export type BillingTxType =
  | 'MODULE_MONTHLY'
  | 'NUMBER_PURCHASE'
  | 'NUMBER_MONTHLY'
  | 'CALL_MINUTES'
  | 'DLC_REGISTRATION'
  | 'DLC_MONTHLY'
  | 'TOPUP'
  | 'ADJUSTMENT'
  | 'DESKTOP_MODULE'
  | 'DESKTOP_LINUX_TERMINAL'
  | 'DESKTOP_WINDOWS_TERMINAL'

// ── ViewSX self-service subscription (spec §8.5/§12) ──
// A server that never went through self-service signup (legacy/unbilled, or
// invite-only) has `subscription: null` and `billingStatus: null` — no
// enforcement, no Subscription card.

export interface SubscriptionItem {
  productKey: string
  stripePriceId: string
  stripeItemId: string
  quantity: number
}

export interface SubscriptionPendingItem {
  productKey: string
  quantity: number
}

export interface SubscriptionView {
  status: string // raw Stripe status mirror: active | past_due | canceled | incomplete | ...
  cadence: string // 'annual' | 'monthly'
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  items: SubscriptionItem[]
  pendingItems: SubscriptionPendingItem[] | null
  graceUntil: string | null
}

export interface BillingSummary {
  balanceCents: number
  currency: string
  telephonyActivatedAt: string | null
  telephonyDeactivatedAt: string | null
  anchorDay: number | null
  nextBillingDate: string | null // ISO date (YYYY-MM-DD)
  mtdSpendCents: number // charges are negative in the journal → reported positive
  subscription: SubscriptionView | null
  // Server.billingStatus: null = legacy/unbilled (no enforcement); else
  // 'active' | 'past_due' | 'canceled_grace' | 'suspended'.
  billingStatus: string | null
}

export interface SubscriptionSeatsResult {
  added?: true
  unchanged?: true
  applied?: 'immediate' | 'next_period'
}

/** POST /api/servers/:serverId/billing/subscription/seats (spec §8.5).
 *  Standalone (not part of serverBillingApi's factory) since every call
 *  site already has serverId in hand from the panel it's rendered in. */
export function postSubscriptionSeats(
  serverId: string,
  productKey: string,
  quantity: number,
): Promise<SubscriptionSeatsResult> {
  return api
    .post(`/servers/${serverId}/billing/subscription/seats`, { productKey, quantity })
    .then((r) => r.data)
}

export interface BillingTransactionItem {
  id: string
  type: BillingTxType
  amountCents: number // negative = charge, positive = top-up/credit
  balanceAfterCents: number
  description: string
  meta: unknown
  createdAt: string
}

export interface BillingTransactionsPage {
  items: BillingTransactionItem[]
  nextCursor: string | null
}

export interface OrgCard {
  orgLegalName: string | null
  orgAddress: string | null
  orgTaxId: string | null
  orgContactName: string | null
  orgContactEmail: string | null
  orgContactPhone: string | null
}

export interface PaymentMethod {
  id: string
  kind: string // 'card' is the only kind today
  label: string
  last4: string | null
  isDefault: boolean
  createdAt: string
}

export interface AddPaymentMethodBody {
  kind: 'card'
  label: string
  last4?: string
}

export interface InvoiceRow {
  id: string
  number: string
  periodStart: string
  periodEnd: string
  totalCents: number
  status: string
  issuedAt: string | null
  createdAt: string
}

export const serverBillingApi = (serverId: string) => ({
  // Summary lives at the router ROOT (GET /api/servers/:serverId/billing) —
  // there is no /summary subpath on the server.
  summary: (): Promise<BillingSummary> =>
    api.get(`/servers/${serverId}/billing`).then((r) => r.data),
  transactions: (cursor?: string, limit?: number): Promise<BillingTransactionsPage> =>
    api.get(`/servers/${serverId}/billing/transactions`, { params: { cursor, limit } }).then((r) => r.data),
  org: (): Promise<OrgCard> =>
    api.get(`/servers/${serverId}/billing/org`).then((r) => r.data),
  // Partial update: omitted fields stay untouched (send null to clear one).
  saveOrg: (body: Partial<OrgCard>): Promise<OrgCard> =>
    api.put(`/servers/${serverId}/billing/org`, body).then((r) => r.data),
  paymentMethods: (): Promise<{ paymentMethods: PaymentMethod[] }> =>
    api.get(`/servers/${serverId}/billing/payment-methods`).then((r) => r.data),
  addPaymentMethod: (body: AddPaymentMethodBody): Promise<{ paymentMethod: PaymentMethod }> =>
    api.post(`/servers/${serverId}/billing/payment-methods`, body).then((r) => r.data),
  removePaymentMethod: (id: string): Promise<void> =>
    api.delete(`/servers/${serverId}/billing/payment-methods/${id}`).then(() => undefined),
  invoices: (): Promise<{ invoices: InvoiceRow[] }> =>
    api.get(`/servers/${serverId}/billing/invoices`).then((r) => r.data),
})

// ── Global-admin billing + telephony stats ──
// Mixed mounts are intentional: overview/minutesReport live on
// /api/admin/telephony/*, transactions/invoices on /api/admin/billing/*.

export interface AdminTelephonyOverviewRow {
  serverId: string
  serverName: string // '(deleted server)' when the server row is gone
  balanceCents: number
  telephonyActivatedAt: string | null
  telephonyDeactivatedAt: string | null
  numbers: { local: number; tollFree: number }
  mtdSpendCents: number
  mtdMinutes: number
  lastChargeAt: string | null
}

export interface MinutesReportRow {
  key: string // server name (groupBy=server) or YYYY-MM-DD (groupBy=day)
  minutes: number
  amountCents: number // positive spend for the bucket
  calls: number
}

export interface AdminBillingTransactionItem extends BillingTransactionItem {
  serverId: string
  serverName: string
}

export interface AdminInvoiceRow extends InvoiceRow {
  serverId: string
  serverName: string
}

export const adminBillingApi = {
  overview: (): Promise<{ servers: AdminTelephonyOverviewRow[] }> =>
    api.get('/admin/telephony/overview').then((r) => r.data),
  minutesReport: (params: { from: string; to: string; groupBy?: 'server' | 'day' }): Promise<{ rows: MinutesReportRow[] }> =>
    api.get('/admin/telephony/minutes-report', { params }).then((r) => r.data),
  transactions: (params?: { serverId?: string; from?: string; to?: string; limit?: number }): Promise<{ items: AdminBillingTransactionItem[] }> =>
    api.get('/admin/billing/transactions', { params }).then((r) => r.data),
  invoices: (): Promise<{ invoices: AdminInvoiceRow[] }> =>
    api.get('/admin/billing/invoices').then((r) => r.data),
}
